import React, { useState, useEffect } from 'react';
import { computed, observable, action } from 'mobx';
import { observer } from 'mobx-react';

import { Heading, SvgIcon, Box, HeaderWithClose } from 'components';
import { Close } from 'icons';
import { useClientStore } from 'apps/hooks';

import { useViewerControl } from '../../hooks';
import { UiModes } from '../../config';

import AssemblyNode from './treeNode';
import { TreeStyled } from './tree.styled';

export class AssemblyState {
  constructor(assembly, root = null) {
    this.assembly = assembly;

    if (root && root.GetParent() != null) {
      this.root = root;
    } else {
      this.root = null;
    }

    this._bodies = null;
    this.bom = new Map();

    // scroll
    this.nodeRef = React.createRef();
  }

  // used in bodies groupList (BOM) view
  @computed get bodies() {
    if (this._bodies) return this._bodies;

    this._bodies = [];
    const { bodies } = this.assembly;
    if (bodies && bodies.size() > 0) {
      if (this.root != null) {
        const instances = this.root.GetInstancesBelow();
        if (instances.size() > 0) {
          // eslint-disable-next-line no-restricted-syntax
          for (const instanceWasm of instances.keys()) {
            const instance = this.assembly.GetInstanceById(instanceWasm?.uuid);
            const body = instance?.GetBody();
            if (body) {
              if (!this.bom.has(body.uuid)) {
                // new body...
                this.bom.set(body.uuid, [instance]);
                this._bodies.push(body);
              } else {
                this.bom.get(body.uuid).push(instance);
              }
            }
          }
        }
      } else {
        // eslint-disable-next-line no-restricted-syntax
        for (const key of bodies.keys()) {
          if (!this.bom.has(key)) {
            // new body...
            const instances = this.assembly.GetInstancesByBodyId(key);
            const n = instances.size();
            for (let i = 0; i < n; i++) {
              if (i === 0) {
                this.bom.set(key, [instances.get(i)]);
              } else {
                this.bom.get(key).push(instances.get(i));
              }
            }

            this._bodies.push(bodies.get(key));
          }
        }
      }

      // sort (???)
      this._bodies = this._bodies.sort((a, b) => {
        const n1 = this.bom.get(a.uuid).length;
        const n2 = this.bom.get(b.uuid).length;
        if (n2 < n1) return -1;
        return 1;
      });
    }

    return this._bodies;
  }

  // mobile kebab menu
  @observable kebabNodeId;

  @action SetExpandedId = newId => {
    this.kebabNodeId = newId;
  };
}

const AssemblyTree = observer(function _AssemblyTree(props) {
  const viewerControl = useViewerControl();
  const clientStore = useClientStore();

  const { root } = props;

  const { settings } = viewerControl.viewer;
  const currCamera = settings?.CurrentCamera();
  const currCamIx = currCamera?.GetIndex();

  const assembly = viewerControl?.cadFile?.assembly;

  const [assemblyState, setAssemblyState] = useState(
    new AssemblyState(assembly, root),
  );

  const handleCloseBtn = () => {
    viewerControl.UpdateUiMode(UiModes.collapsed);
  };

  // tab change
  useEffect(() => {
    if (!currCamera) return;

    if (currCamera.HasSubAssembly()) {
      const newRoot = currCamera.GetSubAssembly();
      setAssemblyState(new AssemblyState(assembly, newRoot));
    } else {
      setAssemblyState(new AssemblyState(assembly, null));
    }
  }, [currCamIx]);

  if (!root) return null;

  return (
    <TreeStyled
      style={{ minWidth: 260, width: '100%' }}
      ref={assemblyState.nodeRef}
    >
      {clientStore.isMobile && (
        <Box
          px={4}
          py={2}
          sx={{
            justifyContent: 'space-between',
            alignItems: 'center',
            display: 'flex',
          }}
        >
          <Heading size='medium'>Assembly Tree</Heading>
          <SvgIcon icon={<Close />} size='large' onClick={handleCloseBtn} />
        </Box>
      )}
      <AssemblyNode cadItem={root} level={0} assemblyState={assemblyState} />
    </TreeStyled>
  );
});

export default AssemblyTree;
